import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ChartSkeleton } from '@/components/ui';
import { AlertCircle, BarChart3 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { PendingStepsData } from '../api/dashboard.schemas';

interface PendingStepsChartProps {
  data?: PendingStepsData;
  loading?: boolean;
  error?: Error;
}

/**
 * PendingStepsChart Component
 * 
 * Displays a horizontal bar chart showing the breakdown of pending verification steps.
 * Shows where users are getting stuck in the verification process.
 */
export function PendingStepsChart({ 
  data, 
  loading = false, 
  error
}: PendingStepsChartProps) {
  const { t } = useTranslation();
  
  // Loading state
  if (loading) {
    return (
      <Card className="h-full flex flex-col">
        <CardHeader className="flex-shrink-0">
          <div className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-primary" />
            <CardTitle className="text-base">{t('dashboard.verificationStepsBreakdown')}</CardTitle>
          </div>
          <CardDescription>{t('dashboard.whereUsersAreInProcess')}</CardDescription>
        </CardHeader>
        <CardContent className="flex-1">
          <ChartSkeleton height={280} showLegend={true} />
        </CardContent>
      </Card>
    );
  }

  // Error state
  if (error) {
    return (
      <Card className="h-full flex flex-col">
        <CardHeader className="flex-shrink-0">
          <div className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-primary" />
            <CardTitle className="text-base">{t('dashboard.chartError')}</CardTitle>
          </div>
          <CardDescription>{t('dashboard.failedToLoadVerificationSteps')}</CardDescription>
        </CardHeader>
        <CardContent className="flex-1 flex items-center justify-center">
          <div className="text-center space-y-4">
            <div className="mx-auto w-12 h-12 bg-gradient-to-br from-destructive/10 to-destructive/5 rounded-xl flex items-center justify-center">
              <AlertCircle className="h-6 w-6 text-destructive/60" />
            </div>
            <div className="space-y-2">
              <h3 className="font-medium">{t('dashboard.unableToDisplayChart')}</h3>
              <p className="text-sm text-muted-foreground">{error.message}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // No data state
  if (!data || data.data.length === 0) {
    return (
      <Card className="h-full flex flex-col">
        <CardHeader className="flex-shrink-0">
          <div className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-primary" />
            <CardTitle className="text-base">{t('dashboard.verificationStepsBreakdown')}</CardTitle>
          </div>
          <CardDescription>{t('dashboard.whereUsersAreInProcess')}</CardDescription>
        </CardHeader>
        <CardContent className="flex-1 flex items-center justify-center">
          <div className="text-center text-muted-foreground">
            <BarChart3 className="h-6 w-6 mx-auto mb-2" />
            <p>{t('dashboard.noPendingVerificationData')}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Sort data by value (highest to lowest) and calculate total
  const sortedData = [...data.data].sort((a, b) => b.value - a.value);
  const total = data.data.reduce((sum, item) => sum + item.value, 0);
  const maxValue = Math.max(...data.data.map(item => item.value));

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="flex-shrink-0">
        <div className="flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-primary" />
          <CardTitle className="text-base">{t('dashboard.verificationStepsBreakdown')}</CardTitle>
        </div>
        <CardDescription>
          {t('dashboard.whereUsersAreInProcessWithTotal', { total })}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col">
        <div className="flex-1 space-y-4 min-h-0">
          {sortedData.map((item, index) => {
            const percentage = total > 0 ? (item.value / total) * 100 : 0;
            const barWidth = maxValue > 0 ? (item.value / maxValue) * 100 : 0;
            
            return (
              <div key={index} className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium">{item.name}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">
                      {item.value} ({percentage.toFixed(1)}%)
                    </span>
                  </div>
                </div>
                <div className="w-full bg-muted rounded-full h-3">
                  <div
                    className="bg-gradient-to-r from-primary to-primary/80 h-3 rounded-full transition-all duration-500 ease-out"
                    style={{ width: `${barWidth}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}